import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unionBy } from 'lodash-es';
import { Case } from '@/api/apis/case';
import { getUnosId } from '@/utils/helpers';

type UseCandidatesListReturnType = {
  searchText: string;
  list: Case[];
  filteredList: Case[];
  activeIds: Set<string>;
  onSearch: (value: string) => void;
};

export const useCandidatesList = (caseList: Case[] = [], caseIdToFilter: string = ''): UseCandidatesListReturnType => {
  const [searchText, setSearchText] = useState('');
  const baseCaseList = useRef<Case[]>([]);

  useEffect(() => {
    baseCaseList.current = unionBy(baseCaseList.current, caseList, 'caseId');
  }, [caseList]);

  const list = useMemo((): Case[] => {
    const baseList = baseCaseList.current.length ? baseCaseList.current : caseList;

    const cases = baseCaseList.current.length ? unionBy(baseList, caseList, 'caseId') : caseList;

    if (caseIdToFilter) return cases.filter((c) => c.caseId !== caseIdToFilter);

    return cases;
  }, [caseList, caseIdToFilter]);

  const activeIds = useMemo((): Set<string> => {
    return new Set(caseList.map((c) => c.caseId));
  }, [caseList]);

  const filteredList = useMemo(() => {
    if (!searchText) return list;

    const lowerSearchText = searchText.toLowerCase().trim();

    return list.filter(
      ({
        donorId,
        organType,
        reAllocationSequenceNumber,
        opoRequest,
        isExpeditedCase,
        laterality,
        duplicateCaseNumber,
      }) => {
        const unosId = getUnosId(
          donorId,
          organType,
          reAllocationSequenceNumber,
          opoRequest,
          isExpeditedCase,
          laterality,
          duplicateCaseNumber,
        );

        return unosId.toLowerCase().includes(lowerSearchText);
      },
    );
  }, [list, searchText]);

  const onSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  return {
    searchText,
    list,
    filteredList,
    activeIds,
    onSearch,
  };
};
